import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { X, ShoppingBag, MapPin, Clock } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Social Proof Popup - Shows recent activity
export const SocialProofPopup = () => {
  const [visible, setVisible] = useState(false);
  const [notification, setNotification] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  // Sample data - In production this comes from API
  const sampleNotifications = [
    { name: 'María', city: 'Madrid', device: 'Nintendo Switch', action: 'solicitó reparación', time: '2 minutos' },
    { name: 'Carlos', city: 'Barcelona', device: 'PlayStation 5', action: 'envió su consola', time: '5 minutos' },
    { name: 'Ana', city: 'Valencia', device: 'iPhone 14', action: 'recogió su dispositivo', time: '8 minutos' },
    { name: 'Pedro', city: 'Sevilla', device: 'Roomba j7+', action: 'solicitó presupuesto', time: '12 minutos' },
    { name: 'Laura', city: 'Málaga', device: 'Xbox Series X', action: 'valoró con 5 estrellas', time: '15 minutos' },
    { name: 'Miguel', city: 'Bilbao', device: 'Samsung Galaxy S24', action: 'solicitó reparación', time: '18 minutos' },
    { name: 'Carmen', city: 'Zaragoza', device: 'Conga 5090', action: 'recibió presupuesto', time: '22 minutos' },
    { name: 'Javier', city: 'Alicante', device: 'MacBook Pro', action: 'envió su portátil', time: '25 minutos' }
  ];

  useEffect(() => {
    if (dismissed) return;

    // Initial delay before showing first popup
    const initialDelay = setTimeout(() => {
      showRandomNotification();
    }, 8000);

    return () => clearTimeout(initialDelay);
  }, [dismissed]);

  useEffect(() => {
    if (!visible || dismissed) return;

    // Auto-hide after 5 seconds
    const hideTimer = setTimeout(() => {
      setVisible(false);
    }, 5000);

    // Show next notification after hide
    const nextTimer = setTimeout(() => {
      showRandomNotification();
    }, 15000 + Math.random() * 10000); // 15-25 seconds

    return () => {
      clearTimeout(hideTimer);
      clearTimeout(nextTimer);
    };
  }, [visible, dismissed]);

  const showRandomNotification = async () => {
    if (dismissed) return;

    try {
      // Try to fetch real data from API
      const response = await axios.get(`${API_URL}/api/social-proof/recent`);
      if (response.data?.notification) {
        setNotification(response.data.notification);
      } else {
        // Fallback to sample data
        const randomIndex = Math.floor(Math.random() * sampleNotifications.length);
        setNotification(sampleNotifications[randomIndex]);
      }
      setVisible(true);
    } catch (error) {
      // Use sample data on error
      const randomIndex = Math.floor(Math.random() * sampleNotifications.length);
      setNotification(sampleNotifications[randomIndex]);
      setVisible(true);
    }
  };

  const handleDismiss = () => {
    setVisible(false);
    setDismissed(true);
    // Store in session to not show again
    sessionStorage.setItem('socialProofDismissed', 'true');
  };

  // Check if previously dismissed
  useEffect(() => {
    if (sessionStorage.getItem('socialProofDismissed') === 'true') {
      setDismissed(true);
    }
  }, []);

  if (!visible || !notification || dismissed) return null;

  return (
    <div 
      className="fixed bottom-20 left-4 z-50 animate-slide-in-left"
      style={{
        animation: 'slideInLeft 0.5s ease-out'
      }}
    >
      <style>{`
        @keyframes slideInLeft {
          from {
            opacity: 0;
            transform: translateX(-100%);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
      `}</style>
      <div className="bg-white rounded-xl shadow-2xl border border-gray-200 p-4 max-w-sm">
        <button 
          onClick={handleDismiss}
          className="absolute top-2 right-2 text-gray-400 hover:text-gray-600"
        >
          <X className="w-4 h-4" />
        </button>
        
        <div className="flex items-start gap-3">
          <div className="w-12 h-12 bg-[#0047FF]/10 rounded-full flex items-center justify-center flex-shrink-0">
            <ShoppingBag className="w-6 h-6 text-[#0047FF]" />
          </div>
          
          <div className="flex-1 pr-4">
            <p className="text-sm">
              <span className="font-semibold">{notification.name}</span>
              {' de '}
              <span className="font-semibold">{notification.city}</span>
              {' '}
              {notification.action}
            </p>
            <p className="text-sm text-[#0047FF] font-medium mt-1">
              {notification.device}
            </p>
            <div className="flex items-center gap-2 mt-2 text-xs text-gray-500">
              <Clock className="w-3 h-3" />
              <span>Hace {notification.time}</span>
              <MapPin className="w-3 h-3 ml-2" />
              <span>{notification.city}</span>
            </div>
          </div>
        </div>
        
        <div className="mt-3 pt-3 border-t text-xs text-gray-500 flex items-center gap-1">
          <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
          <span>Actividad verificada en tiempo real</span>
        </div>
      </div>
    </div>
  );
};

// Urgency Triggers - Shows limited availability
export const UrgencyTriggers = ({ device, city }) => {
  const [urgencyData, setUrgencyData] = useState(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Generate urgency data based on device/city
    const spots = Math.floor(Math.random() * 5) + 1; // 1-5 spots
    const discount = [10, 15, 20, 25][Math.floor(Math.random() * 4)];
    const hours = Math.floor(Math.random() * 48) + 24; // 24-72 hours
    
    setUrgencyData({
      spotsLeft: spots,
      discountPercent: discount,
      expiresIn: hours,
      device: device || 'consolas',
      city: city || 'tu zona'
    });

    // Show after delay
    const timer = setTimeout(() => setVisible(true), 3000);
    return () => clearTimeout(timer);
  }, [device, city]);

  if (!visible || !urgencyData) return null;

  return (
    <div className="bg-gradient-to-r from-orange-500 to-red-500 text-white py-3 px-4">
      <div className="max-w-6xl mx-auto flex items-center justify-center gap-4 text-sm md:text-base">
        <div className="flex items-center gap-2">
          <span className="animate-pulse">🔥</span>
          <span className="font-semibold">
            ¡Solo quedan {urgencyData.spotsLeft} plazas esta semana para reparación de {urgencyData.device}!
          </span>
        </div>
        <div className="hidden md:flex items-center gap-2 border-l border-white/30 pl-4">
          <span>Oferta: {urgencyData.discountPercent}% descuento</span>
          <span className="bg-white/20 px-2 py-0.5 rounded text-xs">
            Expira en {urgencyData.expiresIn}h
          </span>
        </div>
      </div>
    </div>
  );
};

// Stock/Availability Indicator
export const AvailabilityIndicator = ({ available = true, quantity = 3 }) => {
  if (!available) {
    return (
      <div className="flex items-center gap-2 text-red-500 text-sm">
        <div className="w-2 h-2 bg-red-500 rounded-full"></div>
        <span>No disponible temporalmente</span>
      </div>
    );
  }

  if (quantity <= 3) {
    return (
      <div className="flex items-center gap-2 text-orange-500 text-sm">
        <div className="w-2 h-2 bg-orange-500 rounded-full animate-pulse"></div>
        <span className="font-medium">¡Solo {quantity} técnicos disponibles!</span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 text-green-500 text-sm">
      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
      <span>Disponible - Respuesta en 24h</span>
    </div>
  );
};

// Countdown Timer
export const CountdownTimer = ({ endDate, onExpire }) => {
  const [timeLeft, setTimeLeft] = useState({});

  useEffect(() => {
    const calculateTimeLeft = () => {
      const difference = new Date(endDate) - new Date();
      
      if (difference > 0) {
        return {
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((difference / 1000 / 60) % 60),
          seconds: Math.floor((difference / 1000) % 60)
        };
      }
      
      if (onExpire) onExpire();
      return null;
    };

    setTimeLeft(calculateTimeLeft());
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    return () => clearInterval(timer);
  }, [endDate, onExpire]);

  if (!timeLeft) return null;

  return (
    <div className="flex items-center gap-2">
      <span className="text-sm font-medium">Oferta termina en:</span>
      <div className="flex gap-1">
        {timeLeft.days > 0 && (
          <span className="bg-red-500 text-white px-2 py-1 rounded text-sm font-mono">
            {timeLeft.days}d
          </span>
        )}
        <span className="bg-red-500 text-white px-2 py-1 rounded text-sm font-mono">
          {String(timeLeft.hours).padStart(2, '0')}h
        </span>
        <span className="bg-red-500 text-white px-2 py-1 rounded text-sm font-mono">
          {String(timeLeft.minutes).padStart(2, '0')}m
        </span>
        <span className="bg-red-500 text-white px-2 py-1 rounded text-sm font-mono">
          {String(timeLeft.seconds).padStart(2, '0')}s
        </span>
      </div>
    </div>
  );
};

// Recently Viewed Products
export const RecentlyViewed = ({ currentSlug }) => {
  const [items, setItems] = useState([]);

  useEffect(() => {
    // Get from localStorage
    const viewed = JSON.parse(localStorage.getItem('recentlyViewed') || '[]');
    setItems(viewed.filter(item => item.slug !== currentSlug).slice(0, 4));
  }, [currentSlug]);

  if (items.length === 0) return null;

  return (
    <div className="bg-gray-50 rounded-lg p-4">
      <h4 className="font-semibold mb-3">Visto recientemente</h4>
      <div className="flex gap-2 overflow-x-auto">
        {items.map((item, idx) => (
          <a 
            key={idx} 
            href={item.url}
            className="flex-shrink-0 bg-white rounded-lg p-3 border hover:border-blue-500 transition-colors"
          >
            <div className="text-sm font-medium">{item.name}</div>
            <div className="text-xs text-gray-500">{item.price}</div>
          </a>
        ))}
      </div>
    </div>
  );
};

export default SocialProofPopup;
